import Button from "components/lib/Shared/Button";
import { ChevronLeftIcon } from "components/lib/Shared/Icons/sflIcons";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import ManualTransactionEditContext from "context/ManualTransactionEditContext";
import UpdateHistoryContext from "context/UpdateHistoryContext";
import {
  addYears,
  format,
  getYear,
  isBefore,
  lastDayOfMonth,
  setDate,
  setMonth,
} from "date-fns";
import { Field, Form, Formik } from "formik";
import { useToast } from "hooks/useToast";
import _ from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  createAnnualReturnAsync,
  updateAnnualReturnAsync,
} from "state/slices/annualReturn";
import {
  createFinancialStatementAsync,
  updateFinancialStatementAsync,
} from "state/slices/financialStatement";
import {
  updateTemplateFileAsync,
  uploadTemplateFileAsync,
} from "state/slices/templateFile";
import { handleFilesUpload } from "utils/filesUtils";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import * as Yup from "yup";
import { annualReturnsSchema } from "./annualCompliance/annualReturnsSchema";
import { financialStatementsSchema } from "./annualCompliance/financialStatementsSchema";
import AnnualReturns from "./annualCompliance/AnnualReturns";
import FinancialStatements from "./annualCompliance/FinancialStatements";

const AnnualCompliance = ({ handleSummaryNavigation }) => {
  const {
    isInUpdateHistoryContext,
    selectionsMade,
    isStrikeOffSelected,
    setIsStrikeOffSelected,
    createdTransactionsIds,
    setCreatedTransactionsIds,
    addUniqueTransactionId,
    transactionExists,
    transactions,
    dateFrom,
    dateTo,
  } = useContext(UpdateHistoryContext);

  const { transactionToEdit, isInManualTransactionEditContext } = useContext(
    ManualTransactionEditContext
  );

  const {
    isUpdateHistory,
    dateTo: To,
    dateFrom: From,
    historyTransactions,
    period,
  } = useSelector((state) => state.transferYourBusinessSlice);

  const { batchNo } = useSelector((state) => state.batchNumberSlice);

  const tabs = useMemo(() => {
    const commonTabs = [
      {
        label: "Annual Returns",
        id: "annualReturns",
      },
      {
        label: "Financial Statements",
        id: "financialStatements",
      },
    ];
    return commonTabs.filter((tab) => {
      if (tab.id === "annualReturns") {
        return selectionsMade?.summary?.annualCompliance?.includes(
          "annualReturns"
        );
      } else if (tab.id === "financialStatements") {
        return selectionsMade?.summary?.annualCompliance?.includes(
          "financialStatements"
        );
      }
      return false;
    });
  }, []);

  const { company } = useSelector((state) => state.companySlice);
  const { toast } = useToast();
  const { companyId } = useParams();

  const STORAGE_KEY = `annualCompliance_${companyId}`;

  const dispatch = useDispatch();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const isLastTab = selectedTabIndex === tabs.length - 1;
  const isFirstTab = selectedTabIndex === 0;

  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex, tabs]);

  //Company financial statements acountingEndMonth
  const accountingEndMonth =
    company?.primaryBusinessActivity?.accountingEndMonth || "";

  // Generate initial values based on company registration date
  const registrationDate = new Date(company?.registrationDate);
  const day = format(registrationDate, "dd");
  const month = format(registrationDate, "MM");
  const registrationYear = parseInt(format(registrationDate, "yyyy"));
  const currentDate = new Date();

  // 1. Annual Returns Year Options
  const annualReturnsYearOptions = (() => {
    const options = [];
    let currentAnnualReturnDate = addYears(registrationDate, 1); // Start 1 year after registration

    // Generate options until we reach or exceed current date
    while (
      isBefore(currentAnnualReturnDate, currentDate) ||
      format(currentAnnualReturnDate, "yyyy-MM-dd") ===
        format(currentDate, "yyyy-MM-dd")
    ) {
      const dateStr = format(currentAnnualReturnDate, "yyyy/MM/dd");
      options.push({ label: dateStr, value: dateStr });

      // Move to next year
      currentAnnualReturnDate = addYears(currentAnnualReturnDate, 1);
    }

    return options;
  })().reverse();

  // Check if annual returns are not yet available
  const areAnnualReturnsAvailable = annualReturnsYearOptions.length > 0;
  const nextAnnualReturnDate = areAnnualReturnsAvailable
    ? null
    : format(addYears(registrationDate, 1), "yyyy-MM-dd");

  // 2. Financial Statements Year Options
  const financialStatementYearOptions = (() => {
    const options = [];

    if (!accountingEndMonth) return options;

    const accountingMonthIndex = parseInt(accountingEndMonth) - 1; // Convert to 0-indexed month

    // Create the first financial year end date
    let currentFinancialYearEnd = setMonth(
      setDate(registrationDate, 31), // Set to last day of month
      accountingMonthIndex
    );

    // If this date is before registration date, move to next year
    if (isBefore(currentFinancialYearEnd, registrationDate)) {
      currentFinancialYearEnd = addYears(currentFinancialYearEnd, 1);
    }

    // Generate options until we reach or exceed current date
    while (
      isBefore(currentFinancialYearEnd, currentDate) ||
      format(currentFinancialYearEnd, "yyyy-MM-dd") ===
        format(currentDate, "yyyy-MM-dd")
    ) {
      const yearValue = getYear(currentFinancialYearEnd);
      options.push({
        label: yearValue.toString(),
        value: yearValue,
      });

      // Move to next year
      currentFinancialYearEnd = addYears(currentFinancialYearEnd, 1);
    }
    return options;
  })().reverse();

  // Check if financial statements are not yet available
  const areFinancialStatementsAvailable =
    financialStatementYearOptions.length > 0;
  const nextFinancialStatementDate = (() => {
    if (areFinancialStatementsAvailable || !accountingEndMonth) return null;

    const accountingMonthIndex = parseInt(accountingEndMonth) - 1;
    let nextDate = setMonth(
      setDate(registrationDate, 31),
      accountingMonthIndex
    );

    if (isBefore(nextDate, registrationDate)) {
      nextDate = addYears(nextDate, 1);
    }

    return format(nextDate, "yyyy");
  })();

  const annualReturns = (() => {
    if (isInManualTransactionEditContext) {
      return transactionToEdit?.annualReturns || [];
    }

    return company?.annualReturns || [];
  })();

  const lastAnnualReturnYear =
    annualReturns?.length > 0 && !isInManualTransactionEditContext
      ? format(new Date(annualReturns[0]?.dateOfReturn), "yyyy")
      : parseInt(format(new Date(company?.registrationDate), "yyyy")) + 1;

  const filteredAnnualReturnYearOptions = annualReturnsYearOptions?.filter(
    (option) =>
      annualReturns?.length > 0 && !isInManualTransactionEditContext
        ? parseInt(option.value.split("/")[0]) > parseInt(lastAnnualReturnYear)
        : parseInt(option.value.split("/")[0]) >= parseInt(lastAnnualReturnYear)
  );
  // Financial Statements data
  const financialStatements = isInManualTransactionEditContext
    ? transactionToEdit?.financialStatements || []
    : company?.financialStatements || [];

  // Calculate lastFinancialStatementYear using accounting end month
  const calculateLastFinancialStatementYear = () => {
    if (financialStatements?.length > 0) {
      const years = financialStatements.map((statement) =>
        getYear(new Date(statement.yearOfStatement))
      );
      const sortedYears = years.sort((a, b) => a - b);
      return sortedYears[0];
    }

    // If no financial statements exist, calculate the first available year using accounting end month
    if (!accountingEndMonth)
      return parseInt(format(new Date(company?.registrationDate), "yyyy")) + 1;

    const accountingMonthIndex = parseInt(accountingEndMonth) - 1;

    // Create the first financial year end date
    let firstFinancialYearEnd = setMonth(
      setDate(registrationDate, 31),
      accountingMonthIndex
    );

    // If this date is before registration date, move to next year
    if (isBefore(firstFinancialYearEnd, registrationDate)) {
      firstFinancialYearEnd = addYears(firstFinancialYearEnd, 1);
    }

    return getYear(firstFinancialYearEnd);
  };

  const [lastFinancialStatementYear, setLastFinancialStatementYear] = useState(
    calculateLastFinancialStatementYear()
  );

  // Calculate filteredFinancialYearOptions directly
  const filteredFinancialYearOptions = useMemo(() => {
    return (
      financialStatementYearOptions?.filter((option) =>
        financialStatements?.length > 0 &&
        company?.financialStatements.length > 0
          ? parseInt(option.value) > parseInt(lastFinancialStatementYear)
          : parseInt(option.value) >= parseInt(lastFinancialStatementYear)
      ) || []
    );
  }, [
    financialStatements,
    lastFinancialStatementYear,
    financialStatementYearOptions,
  ]);

  // Load initial values from local storage
  const getPersistedInitialValues = useMemo(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    let persistedValues = {};
    if (savedData) {
      try {
        persistedValues = JSON.parse(savedData);
      } catch (error) {
        console.error("Error parsing local storage data:", error);
      }
    }

    const initialValues = {
      annualReturns: {
        lastAnnualReturnYear: lastAnnualReturnYear,
        returns: annualReturnsYearOptions
          .filter(
            (option) =>
              parseInt(option.value.split("/")[0]) >=
              parseInt(lastAnnualReturnYear)
          )
          .map((option) => {
            const existingReturn = annualReturns.find(
              (ret) =>
                format(new Date(ret.dateOfReturn), "yyyy") ===
                option.value.split("/")[0]
            );
            const persistedReturn =
              persistedValues?.annualReturns?.returns?.find(
                (ret) => ret.year === option.value.split("/")[0]
              );
            if (existingReturn) {
              return {
                annualReturnId: existingReturn._id,
                year: format(new Date(existingReturn.dateOfReturn), "yyyy"),
                isNotAvailable: true,
                dateOfReturn: format(
                  new Date(existingReturn.dateOfReturn),
                  "yyyy/MM/dd"
                ),
                fileUploads: existingReturn?.annualReturn?.fileName
                  ? {
                      annualReturns: existingReturn.annualReturn.fileName,
                      id: existingReturn.annualReturn._id,
                    }
                  : [],
              };
            } else if (persistedReturn) {
              return {
                year: persistedReturn.year,
                isNotAvailable: persistedReturn?.isNotAvailable,
                dateOfReturn: persistedReturn.dateOfReturn || "",
                fileUploads: !_.isEmpty(
                  persistedReturn?.fileUploads?.annualReturns
                )
                  ? persistedReturn?.fileUploads
                  : [],
              };
            }
            return {
              year: format(new Date(option.value), "yyyy"),
              isNotAvailable: true,
              dateOfReturn: "",
              fileUploads: [],
            };
          }),
      },
      financialStatements: {
        lastFinancialStatementYear: lastFinancialStatementYear,
        statements: financialStatementYearOptions
          .filter(
            (option) =>
              parseInt(option.value) >= parseInt(lastFinancialStatementYear)
          )
          .map((option) => {
            const existingStatement = financialStatements.find(
              (stmt) =>
                format(new Date(stmt.yearOfStatement), "yyyy") ===
                option.value.toString()
            );

            const persistedStatement =
              persistedValues?.financialStatements?.statements?.find(
                (stmt) => stmt.year === option.value
              );
            if (existingStatement) {
              return {
                financialStatementId: existingStatement._id,
                year: format(
                  new Date(existingStatement.yearOfStatement),
                  "yyyy"
                ),
                isNotAvailable: true,
                yearOfStatement: format(
                  new Date(existingStatement.yearOfStatement),
                  "yyyy/MM/dd"
                ),
                boardApprovalDate: existingStatement.boardApprovalDate
                  ? format(
                      new Date(existingStatement.boardApprovalDate),
                      "yyyy/MM/dd"
                    )
                  : "",
                auditorApprovalDate: existingStatement.auditorApprovalDate
                  ? format(
                      new Date(existingStatement.auditorApprovalDate),
                      "yyyy/MM/dd"
                    )
                  : "",
                fileUploads: existingStatement?.financialStatements?.fileName
                  ? {
                      financialStatements:
                        existingStatement.financialStatements.fileName,
                      id: existingStatement.financialStatements._id,
                    }
                  : [],
              };
            } else if (persistedStatement) {
              return {
                year: persistedStatement.year,
                isNotAvailable: persistedStatement.isNotAvailable || true,
                yearOfStatement: persistedStatement.yearOfStatement,
                boardApprovalDate: persistedStatement.boardApprovalDate || "",
                auditorApprovalDate:
                  persistedStatement.auditorApprovalDate || "",
                fileUploads: !_.isEmpty(
                  persistedStatement?.fileUploads?.financialStatements
                )
                  ? persistedStatement?.fileUploads
                  : [],
              };
            }
            return {
              year: option.value,
              isNotAvailable: true,
              yearOfStatement: format(
                new Date(
                  lastDayOfMonth(
                    new Date(`${option.value}/${accountingEndMonth}`)
                  )
                ),
                "yyyy/MM/dd"
              ),
              boardApprovalDate: "",
              auditorApprovalDate: "",
              fileUploads: [],
            };
          }),
      },
    };

    if (tabs.length === 1) {
      if (tabs[0].id === "annualReturns") {
        delete initialValues.financialStatements;
      } else if (tabs[0].id === "financialStatements") {
        delete initialValues.annualReturns;
      }
    }

    return initialValues;
  }, [
    lastFinancialStatementYear,
    accountingEndMonth,
    financialStatements,
    annualReturns,
    isInManualTransactionEditContext,
    transactionToEdit,
    tabs,
    lastAnnualReturnYear,
    annualReturnsYearOptions,
    financialStatementYearOptions,
  ]);

  //Dynamic validation based on selected tab
  const validationSchema = useMemo(() => {
    if (selectedTab?.id === "annualReturns") {
      return annualReturnsSchema;
    } else if (selectedTab?.id === "financialStatements") {
      return financialStatementsSchema;
    }
    return Yup.object({});
  }, [selectedTab]);

  // HandleSubmit function
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (selectedTab?.id === "annualReturns") {
        const { returns, lastAnnualReturnYear } = values.annualReturns;
        const createdAnnualReturns = [];

        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS
        );

        if (
          returns.every((returnItem) => returnItem.fileUploads.length === 0)
        ) {
          toast("error", `Atleast one annual return should be uploaded.`);
          return;
        }

        for (const returnItem of returns) {
          const { annualReturnId, year, dateOfReturn, fileUploads } =
            returnItem;
          const { annualReturns: file, id } = fileUploads;

          if (!file) {
            // toast(
            //   "error",
            //   `No file provided for year ${year}. Please upload a file.`
            // );
            continue;
          }

          try {
            let uploadedFileName;
            if (file instanceof File) {
              // Handle new file upload
              const filesToUpload = { annualReturns: file };
              const uploadedFiles = await handleFilesUpload(
                filesToUpload,
                null,
                false
              );

              if (!uploadedFiles?.annualReturns) {
                toast(
                  "error",
                  `There was an error uploading the file for year ${year}. Please try again.`
                );
                throw new Error(`File upload failed for year ${year}`);
              }
              uploadedFileName = uploadedFiles.annualReturns;
            } else {
              // Use pre-existing file name
              uploadedFileName = file;
            }

            if (!uploadedFileName) {
              // toast(
              //   "error",
              //   `No valid file name for year ${year}. Please upload a file.`
              // );
              continue;
            }

            // Find the existing annual return with the same year
            const existingAnnualReturn =
              existingTransaction?.annualReturns?.find((ar) => {
                // Extract year from dateOfReturn and compare
                const existingYear = new Date(ar.dateOfReturn)
                  .getFullYear()
                  .toString();
                return existingYear === year;
              });

            const templatePayload = {
              company: companyId,
              fileType: "AnnualReturn",
              fileName:
                existingAnnualReturn && !(file instanceof File)
                  ? existingAnnualReturn?.annualReturn?.fileName
                  : uploadedFileName,
              modeType: "pre-signed-documents",
              subModeType: "self-resolution-type",
            };

            if (
              (isInManualTransactionEditContext &&
                transactionToEdit?.changeType ===
                  Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS) ||
              existingTransaction
            ) {
              templatePayload._id =
                id || existingAnnualReturn?.annualReturn?._id;
              const result = await dispatch(
                updateTemplateFileAsync({ data: templatePayload })
              );
              if (result?.error) {
                toast(
                  "error",
                  "There was an error updating the template file. Please try again."
                );
                throw new Error("Template file update failed");
              }

              const documentId = result?.payload?.updatedTemplate?._id;
              const payload = {
                company: companyId,
                dateOfReturn,
                annualReturn: documentId,
              };

              const annualReturnDocumentId =
                existingAnnualReturn?._id || annualReturnId;

              const annualReturn = await dispatch(
                updateAnnualReturnAsync({
                  annualReturnId: annualReturnDocumentId,
                  data: payload,
                })
              ).unwrap();

              if (annualReturn?.errors) {
                toast(
                  "error",
                  "There was an error updating the annual return. Please try again."
                );
                throw new Error("Annual return update failed");
              }

              createdAnnualReturns.push(annualReturn?._id);
            } else {
              const result = await dispatch(
                uploadTemplateFileAsync({ data: templatePayload })
              );

              if (result?.error) {
                toast(
                  "error",
                  "There was an error uploading the template file. Please try again."
                );
                throw new Error("Template file upload failed");
              }

              const documentId = result?.payload?.document?._id;
              const payload = {
                company: companyId,
                dateOfReturn,
                annualReturn: documentId,
              };

              const annualReturn = await dispatch(
                createAnnualReturnAsync({ data: payload })
              ).unwrap();

              if (annualReturn?.errors) {
                toast(
                  "error",
                  "There was an error creating the annual return. Please try again."
                );
                throw new Error("Annual return creation failed");
              }

              createdAnnualReturns.push(annualReturn?._id);
            }
          } catch (error) {
            console.error(`Failed to process return for year ${year}:`, error);
            toast(
              "error",
              `Failed to process return for year ${year}: ${
                error.message || "Unknown error"
              }`
            );
            throw error;
          }
        }

        if (createdAnnualReturns.length === 0) {
          toast(
            "error",
            "No valid files were uploaded for annual returns. Please check your inputs."
          );
          return;
        }

        let transactionPayload = {};

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            annualReturns: createdAnnualReturns,
            documentId: transactionToEdit?._id || existingTransaction?._id,
            batchNo,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload
          );

          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
          }
        } else {
          transactionPayload = {
            changeType: Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.ANNUAL_RETURNS,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            annualReturns: createdAnnualReturns,
            batchNo,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );

          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
          }

          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      } else if (selectedTab?.id === "financialStatements") {
        const { statements } = values.financialStatements;
        const createdFinancialStatements = [];

        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS
        );

        if (
          statements.every(
            (statementItem) => statementItem.fileUploads.length === 0
          )
        ) {
          toast("error", `Atleast one financial statement should be uploaded.`);
          return;
        }

        for (const statementItem of statements) {
          const {
            year,
            isNotAvailable,
            yearOfStatement,
            boardApprovalDate,
            auditorApprovalDate,
            fileUploads,
            financialStatementId,
          } = statementItem;

          if (isNotAvailable) {
            continue;
          }

          const { financialStatements: file, id } = fileUploads;

          if (!file) {
            toast(
              "error",
              `No file provided for year ${year}. Please upload a file.`
            );
            continue;
          }

          try {
            let uploadedFileName;
            if (file instanceof File) {
              // Handle new file upload
              const filesToUpload = { financialStatement: file };
              const uploadedFiles = await handleFilesUpload(
                filesToUpload,
                null,
                false
              );

              if (!uploadedFiles?.financialStatement) {
                toast(
                  "error",
                  `There was an error uploading the file for year ${year}. Please try again.`
                );
                throw new Error(`File upload failed for year ${year}`);
              }
              uploadedFileName = uploadedFiles.financialStatement;
            } else {
              // Use pre-existing file name
              uploadedFileName = file;
            }

            if (!uploadedFileName) {
              toast(
                "error",
                `No valid file name for year ${year}. Please upload a file.`
              );
              continue;
            }

            const existingFinancialStatement =
              existingTransaction?.financialStatements?.find((f) => {
                const existinYear = new Date(f.yearOfStatement).getFullYear();
                return existinYear === year;
              });
            const templatePayload = {
              company: companyId,
              fileType: "FinancialStatement",
              fileName:
                existingFinancialStatement && !(file instanceof File)
                  ? existingFinancialStatement?.financialStatements?.fileName
                  : uploadedFileName,
              modeType: "pre-signed-documents",
              subModeType: "self-resolution-type",
            };

            if (
              (isInManualTransactionEditContext &&
                transactionToEdit?.changeType ===
                  Constants.TRANSACTION_CHANGE_TYPES
                    .YYYY_FINANCIAL_STATEMENTS) ||
              existingTransaction
            ) {
              templatePayload._id =
                existingFinancialStatement?.financialStatements?._id || id;

              const result = await dispatch(
                updateTemplateFileAsync({ data: templatePayload })
              );

              if (result?.error) {
                toast(
                  "error",
                  "There was an error updating the template file. Please try again."
                );
                throw new Error("Template file update failed");
              }

              const documentId = result?.payload?.updatedTemplate?._id;
              const payload = {
                company: companyId,
                yearOfStatement,
                financialStatements: documentId,
              };

              const financialStatementDocumentId =
                existingFinancialStatement?._id || financialStatementId;

              const financialStatements = await dispatch(
                updateFinancialStatementAsync({
                  financialStatementId: financialStatementDocumentId,
                  data: payload,
                })
              ).unwrap();

              if (financialStatements?.errors) {
                toast(
                  "error",
                  "There was an error updating the Financial Statement. Please try again."
                );
                throw new Error("Financial Statement update failed");
              }

              createdFinancialStatements.push(financialStatements?._id);
            } else {
              const result = await dispatch(
                uploadTemplateFileAsync({ data: templatePayload })
              );

              if (result?.error) {
                toast(
                  "error",
                  "There was an error uploading the template file. Please try again."
                );
                throw new Error("Template file upload failed");
              }

              const documentId = result?.payload?.document?._id;
              const payload = {
                company: companyId,
                yearOfStatement: yearOfStatement,
                boardApprovalDate,
                auditorApprovalDate,
                financialStatements: documentId,
              };
              const financialStatement = await dispatch(
                createFinancialStatementAsync({ data: payload })
              ).unwrap();

              if (financialStatement?.errors) {
                toast(
                  "error",
                  "There was an error creating the financial statement. Please try again."
                );
                throw new Error("Financial statement creation failed");
              }

              createdFinancialStatements.push(financialStatement?._id);
            }
          } catch (error) {
            toast(
              "error",
              `Failed to process statement for year ${year}: ${
                error.message || "Unknown error"
              }`
            );
            throw error;
          }
        }

        if (createdFinancialStatements.length === 0) {
          toast(
            "error",
            "No valid files were uploaded for financial statements. Please check your inputs."
          );
          return;
        }

        let transactionPayload = {};

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            documentId: transactionToEdit?._id || existingTransaction?._id,
            financialStatements: createdFinancialStatements,
            batchNo,
            period,
          };
          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload
          );

          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
          }
        } else {
          transactionPayload = {
            changeType:
              Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.FINANCIAL_STATEMENTS,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            financialStatements: createdFinancialStatements,
            batchNo,
            isForHistory: true,
            period,
          };
          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );

          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
          }

          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      if (isLastTab) {
        handleSummaryNavigation("next");
      } else {
        setSelectedTabIndex(selectedTabIndex + 1);
      }
    } catch (error) {
      toast("error", "An error occurred during submission. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (isFirstTab) {
      handleSummaryNavigation("prev");
    } else {
      setSelectedTabIndex(selectedTabIndex - 1);
    }
  };

  //clear local storage
  // useEffect(() => {
  //   localStorage.clear();
  // }, []);

  return (
    <div className="shadow-sm sm:rounded-md sm:overflow-hidden px-4 py-6 space-y-4 bg-white sm:p-6 mt-4">
      <Formik
        initialValues={getPersistedInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          isSubmitting,
          setFieldValue,
          errors,
          touched,
          values,
          setValues,
        }) => {
          // Save form values to local storage whenever they change
          useEffect(() => {
            // Transform fileUploads to avoid serializing File objects
            const sanitizedValues = {
              ...values,
              annualReturns: values.annualReturns
                ? {
                    ...values.annualReturns,
                    returns: values.annualReturns.returns?.map((ret) => ({
                      ...ret,
                      fileUploads: ret.fileUploads
                        ? {
                            annualReturns:
                              ret.fileUploads.annualReturns instanceof File
                                ? ret.fileUploads.annualReturns.name
                                : ret.fileUploads.annualReturns || "",
                            id: ret.fileUploads.id || "",
                          }
                        : [],
                    })),
                  }
                : undefined,
              financialStatements: values.financialStatements
                ? {
                    ...values.financialStatements,
                    statements: values.financialStatements.statements?.map(
                      (stmt) => ({
                        ...stmt,
                        fileUploads: stmt.fileUploads
                          ? {
                              financialStatements:
                                stmt.fileUploads.financialStatements instanceof
                                File
                                  ? stmt.fileUploads.financialStatements.name
                                  : stmt.fileUploads.financialStatements || "",
                              id: stmt.fileUploads.id || "",
                            }
                          : [],
                      })
                    ),
                  }
                : undefined,
            };
            try {
              localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify(sanitizedValues)
              );
            } catch (error) {
              console.error("Error saving to local storage:", error);
            }
          }, [values]);

          useEffect(() => {
            setLastFinancialStatementYear(
              values?.financialStatements?.lastFinancialStatementYear
            );
          }, [values?.financialStatements?.lastFinancialStatementYear]);

          return (
            <Form className="px-4 py-3 bg-white sm:px-6">
              <Tabs
                tabs={tabs}
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={setSelectedTabIndex}
              />

              {/* Display messages when returns/statements are not available */}
              {!areAnnualReturnsAvailable &&
                selectedTab.id === "annualReturns" && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md my-4">
                    <p className="text-blue-800 font-medium">
                      Annual Returns Not Yet Available
                    </p>
                    <p className="text-blue-700 text-sm mt-1">
                      Your first annual return will be due on{" "}
                      {nextAnnualReturnDate}. Please check back after this date
                      to file your annual return.
                    </p>
                  </div>
                )}

              {!areFinancialStatementsAvailable &&
                selectedTab.id === "financialStatements" && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md my-4">
                    <p className="text-blue-800 font-medium">
                      Financial Statements Not Yet Available
                    </p>
                    <p className="text-blue-700 text-sm mt-1">
                      {accountingEndMonth
                        ? `Your first financial statements will be due for the accounting period ending in ${nextFinancialStatementDate}. 
                    Please check back after this date to file your financial statements.`
                        : "Accounting end month is not configured. Please contact support."}
                    </p>
                  </div>
                )}
              {selectedTab?.id === "annualReturns" &&
                areAnnualReturnsAvailable && (
                  <Field
                    name="annualReturns"
                    yearOptions={filteredAnnualReturnYearOptions}
                    component={AnnualReturns}
                  />
                )}
              {selectedTab?.id === "financialStatements" &&
                areFinancialStatementsAvailable && (
                  <Field
                    name="financialStatements"
                    yearOptions={financialStatementYearOptions}
                    lastFinancialStatementYear={lastFinancialStatementYear}
                    filteredFinancialYearOptions={filteredFinancialYearOptions}
                    component={FinancialStatements}
                  />
                )}

              <div className="flex justify-between space-x-2 mt-4">
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="flex items-start float-right p-2 font-semibold rounded text-primary hover:bg-gray-700 hover:text-white"
                >
                  <span className="flex items-center mr-4 h-7">
                    <ChevronLeftIcon
                      className="flex-shrink-0 w-5 h-5"
                      aria-hidden="true"
                    />
                  </span>
                  <p className="font-semibold">Go back</p>
                </button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-sflPrimary border border-transparent rounded-md shadow-sm hover:bg-sflPrimary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {isSubmitting ? "Saving..." : "Save and proceed"}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AnnualCompliance;
